import type { SafeUser } from './types';

const API = '/api/auth';

function rightRotate(value: number, amount: number): number {
  return (value >>> amount) | (value << (32 - amount));
}

function sha256Fallback(message: string): string {
  const bytes = new TextEncoder().encode(message);
  const bitLength = bytes.length * 8;
  const paddedLength = ((bytes.length + 9 + 63) >> 6) << 6;
  const data = new Uint8Array(paddedLength);
  data.set(bytes);
  data[bytes.length] = 0x80;

  const view = new DataView(data.buffer);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);

  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];

  const w = new Uint32Array(64);

  for (let offset = 0; offset < data.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      w[index] = view.getUint32(offset + index * 4, false);
    }

    for (let index = 16; index < 64; index += 1) {
      const s0 = rightRotate(w[index - 15], 7) ^ rightRotate(w[index - 15], 18) ^ (w[index - 15] >>> 3);
      const s1 = rightRotate(w[index - 2], 17) ^ rightRotate(w[index - 2], 19) ^ (w[index - 2] >>> 10);
      w[index] = (w[index - 16] + s0 + w[index - 7] + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;

    for (let index = 0; index < 64; index += 1) {
      const s1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + k[index] + w[index]) >>> 0;
      const s0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }

  return hash.map((part) => part.toString(16).padStart(8, '0')).join('');
}

/** SHA256 pre-hash — matches AI Lab client behavior for unified auth */
async function sha256(message: string): Promise<string> {
  if (globalThis.crypto?.subtle) {
    const buf = new TextEncoder().encode(message);
    const hash = await globalThis.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return sha256Fallback(message);
}

interface AuthResponse {
  success: boolean;
  data?: {
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
    user: SafeUser;
  };
  error?: string;
}

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: SafeUser | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        this.user = JSON.parse(savedUser);
      } catch { /* ignore */ }
    }
  }

  getUser(): SafeUser | null {
    return this.user;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.user;
  }

  isAdmin(): boolean {
    return this.user?.role === 'admin';
  }

  async login(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    const passwordHash = await sha256(password);
    const res = await fetch(`${API}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password: passwordHash }),
    });

    const data: AuthResponse = await res.json();
    if (!data.success || !data.data) {
      return { success: false, error: data.error || 'Ошибка входа' };
    }

    this.setTokens(data.data.accessToken, data.data.refreshToken, data.data.user);
    return { success: true };
  }

  async logout(): Promise<void> {
    if (this.refreshToken) {
      await fetch(`${API}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      }).catch(() => {});
    }
    this.clearTokens();
  }

  async refresh(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = this._doRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      // finally: при исключении промис иначе залипал бы навсегда, и все
      // следующие refresh() возвращали бы тот же отклонённый результат.
      this.refreshPromise = null;
    }
  }

  /** Выполняет fetch с автоматическим refresh при 401 */
  async apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = new Headers(options.headers);
    if (this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }
    if (!headers.has('Content-Type') && options.body) {
      headers.set('Content-Type', 'application/json');
    }

    let res = await fetch(url, { ...options, headers });

    if (res.status === 401 && this.refreshToken) {
      const refreshed = await this.refresh();
      if (refreshed) {
        headers.set('Authorization', `Bearer ${this.accessToken}`);
        res = await fetch(url, { ...options, headers });
      }
    }

    return res;
  }

  private async _doRefresh(): Promise<boolean> {
    // Refresh-токен одноразовый, а повтор старого сервер считает кражей и отзывает
    // ВСЕ сессии пользователя. Две вкладки админки, проснувшиеся одновременно, именно
    // так и разлогинивали друг друга (в audit_log — «refresh_token_reuse_detected»).
    // Web Locks сериализует refresh между вкладками одного origin; где API нет —
    // работаем как раньше.
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return await navigator.locks.request('witelab-auth-refresh', () => this._refreshLocked());
    }
    return this._refreshLocked();
  }

  private async _refreshLocked(): Promise<boolean> {
    // Пока ждали замок, другая вкладка могла уже ротировать пару и положить её
    // в localStorage — тогда наш refreshToken в памяти протух, берём свежую пару.
    const storedRefresh = localStorage.getItem('refreshToken');
    if (!storedRefresh) {
      // В другой вкладке нажали «Выйти» — выходим и здесь, а не шлём отозванный токен.
      if (this.refreshToken) this.clearTokens();
      return false;
    }
    if (storedRefresh !== this.refreshToken) {
      this.refreshToken = storedRefresh;
      this.accessToken = localStorage.getItem('accessToken');
      try {
        this.user = JSON.parse(localStorage.getItem('user') || 'null') ?? this.user;
      } catch { /* оставляем текущего */ }
      return !!this.accessToken;
    }

    try {
      const res = await fetch(`${API}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      const data = (await res.json().catch(() => null)) as AuthResponse | null;
      if (res.ok && data?.success && data.data) {
        this.setTokens(data.data.accessToken, data.data.refreshToken, data.data.user);
        return true;
      }
      // Разлогиниваем только когда сервер ЯВНО отверг токен. 429 от лимитера,
      // 5xx и HTML-заглушка nginx на время рестарта/деплоя раньше тоже вели
      // в clearTokens — админа выкидывало на логин из-за секундного простоя.
      if (res.status === 400 || res.status === 401) {
        this.clearTokens();
      }
      return false;
    } catch {
      // Сеть / сервер перезапускается — сессию не рвём, просто этот запрос не удался.
      return false;
    }
  }

  private setTokens(access: string, refresh: string, user: SafeUser) {
    this.accessToken = access;
    this.refreshToken = refresh;
    this.user = user;
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('user', JSON.stringify(user));
  }

  private clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
}

export const authService = new AuthService();
